// Admin Contact Messages — list, mark read, delete.
import { useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, Mail, MailOpen } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import SpamPurgeButton from "@/components/admin/SpamPurgeButton";

// Quick category filter — matches the prefilled subjects from PortfolioPage
// / ProductFamilyPage. "Autres" catches anything that doesn't match a preset.
const SUBJECT_CATEGORIES = [
  { id: "all",       label: "Tous sujets", match: () => true },
  { id: "portfolio", label: "Portfolio Bêta", match: (s) => /portfolio/i.test(s) },
  { id: "tasting",   label: "Tasting Bêta",   match: (s) => /tasting/i.test(s) },
  { id: "coach",     label: "Coach Bêta",     match: (s) => /coach/i.test(s) },
  { id: "other",     label: "Autres",         match: (s) => !/portfolio|tasting|coach/i.test(s) },
];

export default function AdminContactMessagesPage() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [subjectCat, setSubjectCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.contactMessagesList({ status }).then(setData).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [status]);

  // Filter the messages by the selected subject category client-side. Counts
  // per category are computed on the unfiltered list so we can show the
  // number of matching messages next to each filter pill.
  const allItems = data?.items || [];
  const filteredItems = useMemo(() => {
    const cat = SUBJECT_CATEGORIES.find((c) => c.id === subjectCat) || SUBJECT_CATEGORIES[0];
    return allItems.filter((m) => cat.match(m.subject || ""));
  }, [allItems, subjectCat]);
  const counts = useMemo(() => {
    const out = {};
    for (const c of SUBJECT_CATEGORIES) {
      out[c.id] = allItems.filter((m) => c.match(m.subject || "")).length;
    }
    return out;
  }, [allItems]);

  const open = (m) => {
    // eslint-disable-next-line
    setOpened(m);
    if (m.status === "new") adminApi.contactMessageMarkRead(m.id).then(load);
  };

  const del = (id) => {
    // eslint-disable-next-line
    if (window.confirm("Supprimer ce message ?")) adminApi.contactMessageDelete(id).then(() => { setOpened(null); load(); });
  };

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="admin-contact-messages">
      <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-6">Messages de contact</h1>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="border border-[color:var(--gv-line)] rounded-[5px] px-3 py-1.5 text-sm" data-testid="filter-status">
          <option value="all">Tous</option>
          <option value="new">Non lus</option>
          <option value="read">Lus</option>
        </select>
        {data && (
          <span className="text-sm text-[color:var(--gv-ink)]/65">
            {data.total} message(s) — <strong className="text-[color:var(--gv-pink)]">{data.new_count}</strong> non lu(s)
          </span>
        )}
        <div className="ml-auto">
          <SpamPurgeButton scope="contact" onDone={load} />
        </div>
      </div>

      {/* Subject category filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {SUBJECT_CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setSubjectCat(c.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${
              subjectCat === c.id
                ? "bg-[color:var(--gv-ink)] text-white"
                : "bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-ink)]"
            }`}
            data-testid={`filter-subject-${c.id}`}
          >
            {c.label}
            <span className={`px-1.5 py-0 rounded-full text-[10px] ${
              subjectCat === c.id ? "bg-white/20" : "bg-[color:var(--gv-ink)]/10 text-[color:var(--gv-ink)]/60"
            }`}>
              {counts[c.id] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[color:var(--gv-ink)]/55"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1 bg-white border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden max-h-[70vh] overflow-y-auto">
            {filteredItems.map((m) => (
              <button key={m.id} onClick={() => open(m)}
                      className={`w-full text-left px-3 py-3 border-b border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)] transition-colors ${opened?.id === m.id ? "bg-[color:var(--gv-mist)]" : ""}`}
                      data-testid={`msg-row-${m.id}`}>
                <div className="flex items-start gap-2">
                  {m.status === "new" ? <Mail size={14} className="text-[color:var(--gv-pink)] mt-1 shrink-0" />
                                       : <MailOpen size={14} className="text-[color:var(--gv-ink)]/40 mt-1 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm truncate ${m.status === "new" ? "font-medium text-[color:var(--gv-ink)]" : "text-[color:var(--gv-ink)]/75"}`}>{m.name}</div>
                    <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 truncate">{m.subject}</div>
                    <div className="text-[10.5px] text-[color:var(--gv-ink)]/45 mt-0.5">
                      {(m.created_at || "").slice(0, 16).replace("T", " ")} · {m.lang?.toUpperCase()}
                    </div>
                  </div>
                </div>
              </button>
            ))}
            {!filteredItems.length && (
              <div className="px-4 py-8 text-center text-[color:var(--gv-ink)]/55 text-sm">Aucun message dans cette catégorie.</div>
            )}
          </div>

          <div className="md:col-span-2 bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6">
            {opened ? (
              <div data-testid="msg-detail">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="font-display text-xl text-[color:var(--gv-ink)]">{opened.subject}</h2>
                    <div className="text-[12.5px] text-[color:var(--gv-ink)]/65 mt-1">
                      De <strong>{opened.name}</strong> &lt;<a href={`mailto:${opened.email}`} className="text-[color:var(--gv-pink)]">{opened.email}</a>&gt;
                    </div>
                    <div className="text-[11px] text-[color:var(--gv-ink)]/45 mt-0.5">
                      Langue : {opened.lang?.toUpperCase()} · IP : {opened.ip || "—"} · {(opened.created_at || "").replace("T", " ").slice(0, 19)}
                    </div>
                  </div>
                  <button onClick={() => del(opened.id)} className="text-[color:var(--gv-pink)] hover:opacity-75" data-testid="msg-delete">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="text-[14px] text-[color:var(--gv-ink)] whitespace-pre-wrap leading-relaxed">{opened.message}</div>
                <div className="mt-6 pt-4 border-t border-[color:var(--gv-line)] text-[11px] text-[color:var(--gv-ink)]/55">
                  <strong>Consentement RGPD :</strong> ✓ <em>{opened.consent_text}</em>
                </div>
              </div>
            ) : (
              <div className="text-center text-[color:var(--gv-ink)]/45 py-20 text-sm">Sélectionne un message à gauche.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
